import { IconFileImport, IconList, IconUserBolt } from "@tabler/icons-react";
import { IconGmaps } from "../../../components/Icons/IconGmaps";
import { IconLinkedin } from "../../../components/Icons/IconLinkedin";
import { IconMagileads } from "../../../components/Icons/IconMagileads";
import { IconSalesNavigator } from "../../../components/Icons/IconSalesNavigator";
import { useLanguage } from "../../../stores/LanguageStore";
import { useProfileStore, useSessionStore } from "../../../stores/UserStore";

const icon_props = { style: { height: 28 } };

export function getListMethod(listId) {
  const lang = useLanguage.getState().language;
  const reseller = useSessionStore.getState().reseller;

  let result = [
    {
      icon: <IconUserBolt {...icon_props} />,
      title: lang.contact_lists.import.methods.quick.label,
      text: lang.contact_lists.import.methods.quick.text,
      value: "quick",
      isLinkedin: true,
      isPopular: true,
    },
    {
      icon: <IconGmaps {...icon_props} />,
      title: lang.contact_lists.import.methods.maps.label,
      text: lang.contact_lists.import.methods.maps.text,
      value: "google",
      isPopular: true,
      permission: "displayTargetingGoogle",
    },
    {
      icon: (
        <IconMagileads
          {...icon_props}
          style={{ height: 28, paddingBottom: 6 }}
          size={48}
        />
      ),
      title: lang.contact_lists.import.methods.database.label + reseller?.name,
      text: lang.contact_lists.import.methods.database.text + reseller?.name,
      value: "database",
      isBeta: true,
      permission: "displayTargetingDatabase",
    },
    {
      icon: <IconSalesNavigator {...icon_props} />,
      title: lang.contact_lists.import.methods.sales.label,
      text: lang.contact_lists.import.methods.sales.text,
      value: "linkedin",
      isLinkedin: true,
      isPopular: true,
    },
    {
      icon: <IconSalesNavigator {...icon_props} />,
      title: lang.contact_lists.import.methods.linkedin_companies.label,
      text: lang.contact_lists.import.methods.linkedin_companies.text,
      value: "companies",
      isLinkedin: true,
    },
    {
      icon: <IconLinkedin {...icon_props} />,
      title: lang.contact_lists.import.methods.linkedin_people.label,
      text: lang.contact_lists.import.methods.linkedin_people.text,
      value: "linkedin",
      isLinkedin: true,
    },
    {
      icon: <IconLinkedin {...icon_props} />,
      title: lang.contact_lists.import.methods.linkedin_likers.label,
      text: lang.contact_lists.import.methods.linkedin_likers.text,
      value: "likers",
      isLinkedin: true,
    },
    {
      icon: <IconLinkedin {...icon_props} />,
      title: lang.contact_lists.import.methods.linkedin_commenters.label,
      text: lang.contact_lists.import.methods.linkedin_commenters.text,
      value: "commenters",
      isLinkedin: true,
    },
    {
      icon: <IconLinkedin {...icon_props} />,
      title: lang.contact_lists.import.methods.linkedin_jobs.label,
      text: lang.contact_lists.import.methods.linkedin_jobs.text,
      value: "jobs",
      isLinkedin: true,
    },
    {
      icon: <IconLinkedin {...icon_props} />,
      title: lang.contact_lists.import.methods.linkedin_connections.label,
      text: lang.contact_lists.import.methods.linkedin_connections.text,
      value: "connections",
      isLinkedin: true,
    },
    {
      icon: <IconFileImport {...icon_props} />,
      title: lang.contact_lists.import.methods.manual.label,
      text: lang.contact_lists.import.methods.manual.text,
      value: "file",
    },
  ];

  if (!listId) {
    result.push({
      icon: <IconList {...icon_props} />,
      title: lang.contact_lists.import.methods.empty.label,
      text: lang.contact_lists.import.methods.empty.text,
      value: "empty",
    });
  }

  return result;
}

export function getChangedKeys(obj1, obj2) {
  const changedKeys = [];

  // Get all keys from both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check for keys that are present in obj1 but not in obj2
  for (let key of keys1) {
    if (!(key in obj2)) {
      changedKeys.push(key);
    } else if (obj1[key] !== obj2[key]) {
      changedKeys.push(key);
    }
  }

  // Check for keys that are present in obj2 but not in obj1
  for (let key of keys2) {
    if (!(key in obj1)) {
      changedKeys.push(key);
    }
  }

  return changedKeys;
}

export function getListOrderName(list) {
  const state_details = list.state_details;
  const origin = state_details[state_details.length - 1]
  if (list?.list_type === "google_maps" || origin?.type?.includes("Google")) {
    return "contact_list_fetch_contacts_order_google_maps";
  }
  if (list?.list_type === "linkedin" || origin?.type?.includes("Linkedin")) {
    return "contact_list_fetch_contacts_order_linkedin";
  }
  if (list?.list_type === "imported") {
    return "contact_list_fetch_contacts_order_imported";
  }
  return "contact_list_fetch_contacts_order"
}

// Définition de l'Enum équivalent à \Magileads\Models\Unsubscribers\UnsubscriberType
const UnsubscriberType = Object.freeze({
  LinkedinUrl: 'linkedin_url',
  DomainEmail: 'domain_email',
  Email: 'email',
  NafCode: 'naf_code',
  Siren: 'siren',
  Siret: 'siret',
  Phone: 'phone',
  Company: 'company'
});
export function detectUnsubscriberTypeFromValue(value) {
  value = value.trim();

  if (value === '') {
    return null;
  }

  // 1. LinkedIn URL
  const linkedinRegex = /^https?:\/\/(?:www\.)?linkedin\.com\/(?:in|sales\/lead|sales\/people)\/([^?/\\,]+)(?:$|[/?,].*)/i;
  const linkedinMatch = value.match(linkedinRegex);
  if (linkedinMatch) {
    return {
      unsubscriberType: UnsubscriberType.LinkedinUrl,
      value: 'https://www.linkedin.com/in/' + linkedinMatch[1] + '/'
    };
  }

  // 2. Domain Email (@domaine.com)
  if (value[0] === '@') {
    return {
      unsubscriberType: UnsubscriberType.DomainEmail,
      value: value.replace(/^@/, '').replace(/ /g, '').toLowerCase()
    };
  }

  // 3. Email (Équivalent de filter_var avec FILTER_VALIDATE_EMAIL)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRegex.test(value)) {
    return {
      unsubscriberType: UnsubscriberType.Email,
      value: value.replace(/ /g, '').toLowerCase()
    };
  }

  // 4. Code NAF (4 ou 5 caractères, dont 4 chiffres)
  if ((value.length === 4 || value.length === 5) && /.*[0-9]{4}.*/.test(value)) {
    return {
      unsubscriberType: UnsubscriberType.NafCode,
      value: value.replace(/ /g, '').toUpperCase()
    };
  }

  // Préparation pour Siren/Siret
  const sirenSiretValue = value.replace(/ /g, '');

  // 5. Siren (9 chiffres)
  // Note: /^\d{9}$/ vérifie que ce sont exactement 9 chiffres (plus robuste que is_numeric en JS)
  if (/^\d{9}$/.test(sirenSiretValue)) {
    return {
      unsubscriberType: UnsubscriberType.Siren,
      value: sirenSiretValue
    };
  }

  // 6. Siret (14 chiffres)
  if (/^\d{14}$/.test(sirenSiretValue)) {
    return {
      unsubscriberType: UnsubscriberType.Siret,
      value: sirenSiretValue
    };
  }

  // 7. Phone (entre 9 et 14 chiffres)
  const phoneValue = value.replace(/[^0-9]/g, '');
  if (phoneValue.length >= 9 && phoneValue.length <= 14) {
    return {
      unsubscriberType: UnsubscriberType.Phone,
      value: phoneValue
    };
  }

  // 8. Company (Défaut)
  return {
    unsubscriberType: UnsubscriberType.Company,
    value: value
  };
}